'use client';
import NextLink from 'next/link';
import { useRouter } from 'next/navigation';
import { ComponentProps, useRef, useCallback } from 'react';
type NextLinkDefaultProps = ComponentProps<typeof NextLink>;

const CustomLink = ({ 
  className = '', 
  href, 
  children, 
  prefetch, 
  prefetchOnHover = true, 
  prefetchDelay = 700, // Default 700ms delay
  onMouseEnter, 
  ...rest 
}: NextLinkDefaultProps & {
  prefetchOnHover?: boolean;
  prefetchDelay?: number;
}) => {
  const router = useRouter();
  const prefetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasPrefetchedRef = useRef<boolean>(false);

  const handleMouseEnter = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    if (onMouseEnter) {
      onMouseEnter(e);
    }
    
    if (prefetchOnHover && href && !hasPrefetchedRef.current) {
      // Clear any existing timeout
      if (prefetchTimeoutRef.current) {
        clearTimeout(prefetchTimeoutRef.current);
      }
      
      // Set a new timeout for prefetching
      prefetchTimeoutRef.current = setTimeout(() => {
        router.prefetch(href.toString());
        hasPrefetchedRef.current = true;
      }, prefetchDelay);
    }
  }, [onMouseEnter, prefetchOnHover, href, prefetchDelay, router]);

  const handleMouseLeave = useCallback(() => {
    // Clear the prefetch timeout if user leaves before delay
    if (prefetchTimeoutRef.current) {
      clearTimeout(prefetchTimeoutRef.current);
      prefetchTimeoutRef.current = null;
    }
  }, []);

  return (
    <>
      {href ? (
        <NextLink
          href={href}
          className={className}
          prefetch={prefetch || false}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          {...rest}
        >
          {children}
        </NextLink>
      ) : (
        <span className={className} {...rest}>
          {children}
        </span>
      )}
    </>
  );
};

export default CustomLink;
